import React from "react";
import PageHero from "../components/layout/PageHero";
import Reveal from "../components/Reveal";
import { useContent } from "../context/ContentContext";
import { mediaUrl } from "../api";
import { ShieldCheck, FileCheck2, ClipboardCheck, BadgeCheck, CheckCircle2 } from "lucide-react";

const pillars = [
  { icon: ShieldCheck, title: "Regulatory Expertise", desc: "Partners with accreditations across major global regulatory bodies." },
  { icon: FileCheck2, title: "Dossier & Documentation", desc: "Complete dossier preparation and compliance support for every market." },
  { icon: ClipboardCheck, title: "Product Registration", desc: "End-to-end registration support to enter regulated global markets." },
  { icon: BadgeCheck, title: "Quality Assurance", desc: "Pre-qualified manufacturing partners committed to consistent quality." },
];

export default function Quality() {
  const { content } = useContent();
  const certifications = content.certifications || [];
  const images = content.images || {};
  return (
    <>
      <PageHero
        eyebrow="Quality & Compliance"
        crumb="Quality & Compliance"
        title="Partners with global accreditations"
        subtitle="Quality, manufacturing standards, certifications and regulatory compliance sit at the heart of every sourcing engagement."
      />

      {/* Pillars */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div className="grid sm:grid-cols-2 gap-5 order-2 lg:order-1">
            {pillars.map((p, i) => (
              <Reveal key={p.title} delay={(i % 2) * 90}>
                <div className="h-full bg-card rounded-2xl border border-border p-6">
                  <div className="h-12 w-12 rounded-xl bg-[#0f2236] flex items-center justify-center">
                    <p.icon className="h-6 w-6 text-[#2ED9B8]" strokeWidth={1.8} />
                  </div>
                  <h3 className="mt-5 font-display text-[19px] text-foreground">{p.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="order-1 lg:order-2">
            <div className="rounded-[22px] overflow-hidden ring-1 ring-black/5 shadow-[0_30px_70px_-30px_rgba(12,97,82,0.4)]">
              <img src={mediaUrl(images.labTech)} alt="Quality and compliance" className="w-full h-[460px] object-cover" loading="lazy" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Accreditations grid */}
      <section className="py-16 lg:py-24 bg-[#0f2236]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.2em] text-[#2ED9B8] font-medium">Global certifications</p>
            <h2 className="mt-3 font-display text-[32px] lg:text-[42px] leading-[1.1] text-foreground">
              Accreditations across regulated markets
            </h2>
            <p className="mt-4 text-[15.5px] text-muted-foreground">
              Our manufacturing partners hold accreditations recognised by major regulatory authorities worldwide.
            </p>
          </Reveal>
          <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {certifications.map((c, i) => (
              <Reveal key={c} delay={(i % 4) * 60}>
                <div className="flex items-center gap-3 bg-card rounded-xl border border-white/10 px-5 py-4 hover:border-[#2ED9B8]/25 transition-colors">
                  <CheckCircle2 className="h-5 w-5 text-[#2ED9B8] shrink-0" />
                  <span className="text-sm font-medium text-foreground">{c}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

    </>
  );
}
